import { useEffect, useState, type KeyboardEvent, type ReactNode } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';

/**
 * Tabelle (design.md §7): sticky Kopf, nur horizontale Linien, ganze Zeile ist ein Link (Klick/Enter),
 * erste Spalte bleibt beim horizontalen Scrollen stehen. Laden: Kopf bleibt, nach 200 ms Platzhalterzeilen.
 */

export interface Column<T> {
  key: string;
  header: string;
  cell: (row: T) => ReactNode;
  num?: boolean;
  /** `md`: ab < 1200 px ausblenden, `sm`: ab < 768 px. */
  hide?: 'md' | 'sm';
}

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  rowHref,
  loading,
  empty,
  label,
}: {
  columns: Column<T>[];
  rows: T[] | undefined;
  rowKey: (row: T) => string;
  rowHref?: (row: T) => string;
  loading?: boolean;
  empty: ReactNode;
  /** Zugänglicher Name der Tabelle */
  label: string;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [showPlaceholder, setShowPlaceholder] = useState(false);
  useEffect(() => {
    if (!loading) return setShowPlaceholder(false);
    const t = setTimeout(() => setShowPlaceholder(true), 200);
    return () => clearTimeout(t);
  }, [loading]);

  const hideCls = (c: Column<T>) => [c.num ? 'num' : '', c.hide ? `hide-${c.hide}` : ''].filter(Boolean).join(' ') || undefined;

  // `from` merkt sich die Liste samt Filtern für den Zurück-Link der Detailseite.
  const open = (row: T) => rowHref && navigate(rowHref(row), { state: { from: location.pathname + location.search } });
  const onKey = (e: KeyboardEvent, row: T) => {
    if (e.key === 'Enter') open(row);
  };

  return (
    <div className="table-wrap">
      <table aria-label={label} aria-busy={loading || undefined}>
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c.key} scope="col" className={hideCls(c)}>
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading && showPlaceholder &&
            Array.from({ length: 5 }, (_, i) => (
              <tr key={`ph${i}`} className="placeholder" aria-hidden>
                {columns.map((c) => (
                  <td key={c.key} className={hideCls(c)}>
                    <span className="ph-bar" />
                  </td>
                ))}
              </tr>
            ))}
          {!loading && rows?.length === 0 && (
            <tr className="empty">
              <td colSpan={columns.length}>{empty}</td>
            </tr>
          )}
          {!loading &&
            rows?.map((row) => (
              <tr
                key={rowKey(row)}
                className={rowHref ? 'row' : undefined}
                tabIndex={rowHref ? 0 : undefined}
                onClick={rowHref ? (e) => {
                  // Klicks auf Links/Buttons in der Zeile nicht doppelt behandeln
                  if ((e.target as HTMLElement).closest('a, button')) return;
                  open(row);
                } : undefined}
                onKeyDown={rowHref ? (e) => onKey(e, row) : undefined}
              >
                {columns.map((c) => (
                  <td key={c.key} className={hideCls(c)}>
                    {c.cell(row)}
                  </td>
                ))}
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Keyset-Pagination mit Zurück: Der aktuelle Cursor und die vorherigen stehen in der URL
 * (`cursor`, `back` = durch Punkte getrennt; base64url enthält keinen Punkt). Filter bleiben erhalten.
 */
export function useKeysetPage() {
  const [params, setParams] = useSearchParams();
  const cursor = params.get('cursor') ?? undefined;
  const back = params.get('back')?.split('.').filter(Boolean) ?? [];
  const limit = Number(params.get('limit') ?? 25);

  const update = (next: { cursor?: string; back: string[] }) =>
    setParams((p) => {
      const q = new URLSearchParams(p);
      if (next.cursor) q.set('cursor', next.cursor);
      else q.delete('cursor');
      if (next.back.length) q.set('back', next.back.join('.'));
      else q.delete('back');
      return q;
    });

  return {
    cursor,
    limit,
    pageIndex: back.length + (cursor ? 1 : 0),
    next: (nextCursor: string) => update({ cursor: nextCursor, back: cursor ? [...back, cursor] : back }),
    prev: () => {
      if (!cursor) return;
      const copy = [...back];
      const prevCursor = copy.pop();
      update({ cursor: prevCursor, back: copy });
    },
    setLimit: (n: number) =>
      setParams((p) => {
        const q = new URLSearchParams(p);
        q.set('limit', String(n));
        q.delete('cursor');
        q.delete('back');
        return q;
      }),
  };
}

export function Pager({
  page,
  count,
  nextCursor,
}: {
  page: ReturnType<typeof useKeysetPage>;
  count: number;
  nextCursor: string | null | undefined;
}) {
  const from = page.pageIndex * page.limit + (count ? 1 : 0);
  const to = page.pageIndex * page.limit + count;
  return (
    <nav className="pager" aria-label="Seiten">
      <span>
        {from}–{to}
      </span>
      <select aria-label="Einträge pro Seite" value={page.limit} onChange={(e) => page.setLimit(Number(e.target.value))}>
        {[25, 50, 100].map((n) => (
          <option key={n} value={n}>
            {n} pro Seite
          </option>
        ))}
      </select>
      <button type="button" onClick={page.prev} disabled={!page.cursor}>
        ‹ Zurück
      </button>
      <button type="button" onClick={() => nextCursor && page.next(nextCursor)} disabled={!nextCursor}>
        Weiter ›
      </button>
    </nav>
  );
}

/** Reiter mit Pfeiltasten-Bedienung (design.md §7 Tastatur). */
export function Tabs<K extends string>({
  tabs,
  active,
  onChange,
}: {
  tabs: { key: K; label: string; count?: number }[];
  active: K;
  onChange: (key: K) => void;
}) {
  const onKey = (e: KeyboardEvent<HTMLButtonElement>, i: number) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    const j = (i + (e.key === 'ArrowRight' ? 1 : tabs.length - 1)) % tabs.length;
    onChange(tabs[j]!.key);
    e.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>('[role=tab]')[j]?.focus();
  };
  return (
    <div className="tabs" role="tablist">
      {tabs.map((t, i) => (
        <button
          key={t.key}
          type="button"
          role="tab"
          aria-selected={t.key === active}
          tabIndex={t.key === active ? 0 : -1}
          onClick={() => onChange(t.key)}
          onKeyDown={(e) => onKey(e, i)}
        >
          {t.label}
          {t.count !== undefined && <span className="n">{t.count}</span>}
        </button>
      ))}
    </div>
  );
}

/** Seitenkopf: ein Titel, eine Zeile Kontext, Aktionen rechts (design.md §5). */
export function PageHead({ title, context, actions, back }: { title: ReactNode; context?: ReactNode; actions?: ReactNode; back?: ReactNode }) {
  return (
    <header className="page-head">
      <div>
        {back && <div className="back">{back}</div>}
        <h1>{title}</h1>
        {context && <div className="ctx">{context}</div>}
      </div>
      {actions && <div className="actions">{actions}</div>}
    </header>
  );
}
